import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../api/axiosInstance";
import config from "../../api/config";
import { getTenantId, getAuthSource } from "../../api/apiHelpers";
import { isPublicPagePath } from "../../utils/analyticsPaths";
import { safeSessionGetItem, safeSessionSetItem } from "@mediatechgroup/app-version";

const BASE_URL = config.BASE_URL;

/**
 * Tracks page views on public routes (see utils/analyticsPaths).
 * Renders nothing — just a side-effect component.
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const lastTracked = useRef("");

  useEffect(() => {
    const path = location.pathname;
    // Deduplicate consecutive navigations to the same path
    if (path === lastTracked.current) return;
    lastTracked.current = path;

    // Only public pages are page views. Signing in, the kiosk, the member
    // portal and the staff pages are the club's own people, not visitors
    // (they were most of what was being counted).
    if (!isPublicPagePath(path)) return;

    // Extract UTM params from URL
    const params = new URLSearchParams(location.search);
    const utm_source = params.get("utm_source") || undefined;
    const utm_medium = params.get("utm_medium") || undefined;
    const utm_campaign = params.get("utm_campaign") || undefined;

    // Get or create session ID
    let sessionId = safeSessionGetItem("analytics_session_id");
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      safeSessionSetItem("analytics_session_id", sessionId);
    }

    // Fire and forget
    axios
      .post(
        `${BASE_URL}/analytics/events`,
        {
          event_type: "page_view",
          page_path: path,
          referrer: document.referrer || undefined,
          utm_source,
          utm_medium,
          utm_campaign,
          session_id: sessionId,
        },
        {
          headers: {
            "x-tenant-id": getTenantId(),
            "x-auth-source": getAuthSource(),
          },
        }
      )
      .catch(() => {
        // Silent fail — analytics should never block UX
      });
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
